import { StateCreator } from 'zustand';
import { getBackend } from '@/backend';
import type { AppState } from '../../store/app-state';
import {
    getActivityEntrySummary,
    getActivityGroupCategory,
    getActivityGroupCategoryForEntries,
    getActivityGroupSummary,
} from './presentation';
import type { ActivityLogEntry, ActivityLogFilters, ActivityGroup } from './types';

const ACTIVITY_GROUP_WINDOW_MS = 5 * 60 * 1000;

export interface ActivityLogSlice {
    activityLog: ActivityLogEntry[];
    activityFilters: ActivityLogFilters;
    isLoadingActivity: boolean;
    activityLogActions: {
        // Fetch activity log
        fetchActivityLog: (projectId: string, filters?: ActivityLogFilters) => Promise<void>;
        
        // Update filters
        setActivityFilters: (filters: ActivityLogFilters) => void;
        clearActivityFilters: () => void;
        
        // Smart grouping
        getGroupedActivity: () => ActivityGroup[];
        
        // Clear
        clearActivityLog: () => void;
    };
}

/**
 * Group consecutive similar activities for smart collapse
 */
function groupActivities(entries: ActivityLogEntry[]): ActivityGroup[] {
    if (entries.length === 0) return [];
    
    const groups: ActivityGroup[] = [];
    let currentGroup: ActivityLogEntry[] = [entries[0]!];

    const finalizeGroup = (groupEntries: ActivityLogEntry[]): ActivityGroup => {
        const firstEntry = groupEntries[0]!;
        const category = getActivityGroupCategoryForEntries(groupEntries);
        return {
            id: groupEntries.length > 1 ? `group_${firstEntry.batchId || firstEntry.id}` : firstEntry.id,
            summary: groupEntries.length > 1 ? getActivityGroupSummary(groupEntries) : getActivityEntrySummary(firstEntry),
            entries: groupEntries,
            timestamp: firstEntry.timestamp,
            entityType: firstEntry.entityType,
            category,
            actorName: firstEntry.userName,
            actionType: firstEntry.actionType
        };
    };

    const canGroupActivities = (prev: ActivityLogEntry, curr: ActivityLogEntry): boolean => {
        if (prev.userId !== curr.userId || prev.actionType !== curr.actionType) {
            return false;
        }

        if (prev.batchId && prev.batchId === curr.batchId) {
            return true;
        }

        const timeDiff = Math.abs(prev.timestamp - curr.timestamp);
        return (
            getActivityGroupCategory(prev) === getActivityGroupCategory(curr) &&
            timeDiff < ACTIVITY_GROUP_WINDOW_MS
        );
    };
    
    for (let i = 1; i < entries.length; i++) {
        const prev = entries[i - 1]!;
        const curr = entries[i]!;

        if (canGroupActivities(prev, curr)) {
            currentGroup.push(curr);
        } else {
            groups.push(finalizeGroup(currentGroup));
            currentGroup = [curr];
        }
    }
    
    groups.push(finalizeGroup(currentGroup));
    
    return groups;
}

export const createActivityLogSlice: StateCreator<
    AppState & ActivityLogSlice,
    [["zustand/immer", never]],
    [],
    ActivityLogSlice
> = (set, get) => ({
    activityLog: [],
    activityFilters: {},
    isLoadingActivity: false,
    
    activityLogActions: {
        fetchActivityLog: async (projectId, filters = {}) => {
            set(state => {
                state.isLoadingActivity = true;
            });
            
            try {
                const { cloud } = get();
                if (!cloud.orgId || cloud.projectId !== projectId) {
                    throw new Error('Project organization scope is required to load activity log');
                }
                const activityLogClient = getBackend().activityLog;
                if (!activityLogClient) {
                    throw new Error('Activity log backend client is unavailable');
                }

                const entries = await activityLogClient.getActivityLog({ orgId: cloud.orgId, projectId }, filters);
                
                set(state => {
                    state.activityLog = entries;
                    state.activityFilters = filters;
                    state.isLoadingActivity = false;
                });
                
            } catch (error) {
                console.error('Failed to fetch activity log:', error);
                set(state => {
                    state.isLoadingActivity = false;
                });
            }
        },
        
        setActivityFilters: (filters) => set(state => {
            state.activityFilters = filters;
        }),
        
        clearActivityFilters: () => set(state => {
            state.activityFilters = {};
        }),
        
        getGroupedActivity: () => {
            const state = get();
            return groupActivities(state.activityLog);
        },
        
        clearActivityLog: () => set(state => {
            state.activityLog = [];
            state.activityFilters = {};
        })
    }
});
